import { FormikProps, useFormikContext } from 'formik';
// eslint-disable-next-line no-use-before-define
import React, { useContext, useEffect } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { GET_CAREPLAN } from '../Queries';
import ROPatientCarePlanContext from '../ROPatientCarePlanContext';
import { CAREPLAN_PAGES } from '../Constants';
import { getDiagnosisCode } from '../CarePlanPage';
import { previousURL } from '../TreatmentInfoPage/TreatmentInfoPage';
import { useQuery } from '@apollo/client';
import { useTenantAppConfig } from 'op-utils/useTenantAppConfig';
import { Divider, Stack } from '@mui/material';
import ROStepperSummary from './ROStepperSummary';
import ROStepperSection, { Step } from './ROStepperSection';
import { useCareplanDataContext } from '../CareplanContext/CareplanDataContext';

// no longer using enum page ids for steps

interface Props extends RouteComponentProps<{ id: string; careplanId: string; diagnosisCodeSet?: any }> {
  diagnosisCodeSet?: any;
}

interface StepSection {
  key: string;
  steps: Step[];
}

const ROPatientDiagnosisSteps = (props: Props) => {
  const { id: patientId, careplanId } = props.match.params;
  const { values }: FormikProps<any> = useFormikContext();
  const { state: careplanState } = useContext(ROPatientCarePlanContext);

  const tenantAppConfig = useTenantAppConfig();
  const showSpecialPhysicsConsultPage = tenantAppConfig.SpecialPhysicsConsultVisible;

  const { isMRLApplicable } = useCareplanDataContext();

  const diagnosis = values?.diagnosis;
  // Build TX steps dynamically, then assign sequential indexes starting at Simulation's enum value
  const buildTxSteps = () => {
    const baseSteps: Step[] = [
      { idx: 0, text: 'Simulation', page: CAREPLAN_PAGES.SIMULATION },
      { idx: 0, text: 'Prescription', page: CAREPLAN_PAGES.PRESCRIPTION },
      { idx: 0, text: 'Voluming', page: CAREPLAN_PAGES.VOLUMING },
    ];
    if (isMRLApplicable) {
      baseSteps.push({ idx: 0, text: 'MRL Safety Screening', page: CAREPLAN_PAGES.MRL_SAFETY_SCREENING });
    }
    if (showSpecialPhysicsConsultPage) {
      baseSteps.push({ idx: 0, text: 'Special Physics Consult', page: CAREPLAN_PAGES.SPECIAL_PHYSICS_CONSULT });
    }
    baseSteps.push({ idx: 0, text: 'Submission', page: CAREPLAN_PAGES.SUBMISSION });

    const startIdx = 7; // simulation is the 7th step overall
    return baseSteps.map((s, i) => ({ ...s, idx: startIdx + i }));
  };

  const stepSections = [
    {
      key: 'dx',
      steps: [
        { idx: 1, text: 'Diagnosis selection', page: CAREPLAN_PAGES.CREATE },
        { idx: 2, text: 'Staging', page: CAREPLAN_PAGES.STAGING },
        { idx: 3, text: 'Intake information', page: CAREPLAN_PAGES.INTAKE },
      ],
    },
    {
      key: 'cp',
      steps: [
        { idx: 4, text: 'Treatment information', page: CAREPLAN_PAGES.TREATMENT_INFO },
        { idx: 5, text: 'Careplan selection', page: CAREPLAN_PAGES.TEMPLATE_SELECTION },
        { idx: 6, text: 'Site setup', page: CAREPLAN_PAGES.SITESETUP },
      ],
    },
    {
      key: 'tx',
      steps: buildTxSteps(),
    },
  ];

  const diagnosisCodeSet = getDiagnosisCode(diagnosis);
  const { data: careplanData, error: careplanError } = useQuery(GET_CAREPLAN, {
    variables: { id: careplanId },
    skip: careplanId === 'create',
    fetchPolicy: 'cache-and-network',
  });

  const status = careplanData?.careplan?.careplanStatus || 'DRAFT';
  const statusText = status?.replace('_', ' ').toLowerCase();

  useEffect((): void => {
    const activePanelElements = document.getElementsByClassName('timeline-item current');
    if (!activePanelElements || !activePanelElements.length) return;
    activePanelElements[0].scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'start',
    });
    (activePanelElements[0] as HTMLElement).focus({ preventScroll: true });
  });

  const isExistingDiagnosis =
    !!diagnosis &&
    (!diagnosis?.isPrimaryDiagnosis || !careplanData?.careplan?.isPrimDiagnosisOwner) &&
    status !== 'DRAFT';

  const previousUrl =
    diagnosis && careplanData
      ? previousURL(diagnosis, careplanData?.careplan, patientId, careplanId, careplanState)
      : '';

  const disabled = careplanData?.careplan?.changeType === 'CPOT' && status === 'PRESCRIPTION';

  if (careplanError) {
    return <></>;
  }

  const pathMatch = Object.values(CAREPLAN_PAGES)
    .map((page) => (props.location.pathname.includes(page) ? page : null))
    .filter(Boolean);
  const currentRoute = (pathMatch[0] as Step['page']) || CAREPLAN_PAGES.CREATE;
  const allSteps: Step[] = stepSections.flatMap((s) => s.steps);
  const currentGlobalStep = allSteps.find((s) => s.page === currentRoute);
  const currentGlobalIndex = currentGlobalStep?.idx ?? 1;

  return (
    <Stack>
      <ROStepperSummary status={status} statusText={statusText} diagnosisCodeSet={diagnosisCodeSet} />
      {stepSections.map(
        (stepSection: StepSection, index: number): JSX.Element => (
          <Stack key={index}>
            <Divider sx={{ margin: '8px 0' }} />
            <ROStepperSection
              careplanId={careplanId}
              patientId={patientId}
              diagnosisId={diagnosis?.id}
              steps={stepSection.steps}
              currentStepIndex={currentGlobalIndex}
              isExistingDiagnosis={isExistingDiagnosis}
              previousUrl={previousUrl}
              disabled={disabled}
              statusText={statusText}
            />
          </Stack>
        ),
      )}
    </Stack>
  );
};

export default withRouter(ROPatientDiagnosisSteps);
